import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Hotel, LogOut, Menu, X } from 'lucide-react';

const Navbar: React.FC = () => {
  const { currentUser, logout, isAdmin, logo } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <nav className="bg-indigo-600 text-white shadow-md relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center" onClick={closeMobileMenu}>
              {logo ? (
                <img src={logo} alt="Hotel Logo" className="h-8 w-auto" />
              ) : (
                <div className="flex items-center">
                  <Hotel className="h-8 w-8" />
                  <span className="font-bold text-xl ml-2 hidden sm:block">RoomBooker</span>
                  <span className="font-bold text-lg ml-2 sm:hidden">RB</span>
                </div>
              )}
            </Link>
            
            {/* Desktop navigation */}
            <div className="hidden md:ml-10 md:flex md:items-center md:space-x-4">
              <Link 
                to="/rooms" 
                className="px-3 py-2 rounded-md text-sm font-medium hover:bg-indigo-500 transition-colors duration-200"
              >
                Rooms
              </Link>
            </div>
          </div>

          {/* Desktop user menu */}
          <div className="hidden md:flex md:items-center md:space-x-4">
            {currentUser ? (
              <>
                {isAdmin && (
                  <Link 
                    to="/admin" 
                    className="px-3 py-2 rounded-md text-sm font-medium hover:bg-indigo-500 transition-colors duration-200"
                  >
                    Admin Panel
                  </Link>
                )}
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-indigo-100">
                    Welcome, {currentUser.name}
                  </span>
                  <button
                    onClick={handleLogout}
                    className="flex items-center px-3 py-2 rounded-md text-sm font-medium hover:bg-indigo-500 transition-colors duration-200"
                  >
                    <LogOut className="h-4 w-4 mr-1" />
                    Logout
                  </button>
                </div>
              </>
            ) : (
              <Link 
                to="/login" 
                className="px-3 py-2 rounded-md text-sm font-medium hover:bg-indigo-500 transition-colors duration-200"
              >
                Login
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white p-2"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute top-16 inset-x-0 bg-indigo-600 border-t border-indigo-500 shadow-lg z-50">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <Link
              to="/rooms"
              onClick={closeMobileMenu}
              className="block px-3 py-2 rounded-md text-base font-medium hover:bg-indigo-500 transition-colors duration-200"
            >
              Rooms
            </Link>
            
            {currentUser ? (
              <>
                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 rounded-md text-base font-medium hover:bg-indigo-500 transition-colors duration-200"
                  >
                    Admin Panel
                  </Link>
                )}
                <div className="border-t border-indigo-500 pt-4 pb-3">
                  <div className="px-3 py-2">
                    <div className="text-base font-medium text-white">
                      {currentUser.name}
                    </div>
                    <div className="text-sm text-indigo-200">
                      {currentUser.email}
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-3 py-2 rounded-md text-base font-medium hover:bg-indigo-500 transition-colors duration-200"
                  >
                    <LogOut className="h-4 w-4 mr-2 inline" />
                    Logout
                  </button>
                </div>
              </>
            ) : (
              <Link
                to="/login"
                onClick={closeMobileMenu}
                className="block px-3 py-2 rounded-md text-base font-medium hover:bg-indigo-500 transition-colors duration-200"
              >
                Login
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;